import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ArrowLeft, Send } from "lucide-react";
import { useLogo, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { PublicHeader } from "../components/PublicHeader";
import { PublicFooter } from "../components/PublicFooter";
import { SITE_SCHEMAS } from "../lib/siteSchema";

const Paragraphs = ({ text, className }) => (
  <>
    {String(text || "")
      .split("\n")
      .filter((l) => l.trim())
      .map((p, i) => (
        <p key={i} className={className}>
          {p}
        </p>
      ))}
  </>
);

const renderCards = (field, arr) => {
  if (field.style === "grid") {
    return (
      <div className="grid sm:grid-cols-2 gap-4">
        {arr.map((it, i) => (
          <div key={i} className="p-5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
            <h3 className="font-semibold text-lg mb-1">{it.title}</h3>
            <p className="text-sm text-slate-600 dark:text-slate-300">{it.description}</p>
          </div>
        ))}
      </div>
    );
  }
  if (field.style === "pricing") {
    return (
      <div className="grid sm:grid-cols-3 gap-4">
        {arr.map((it, i) => (
          <div key={i} className="p-6 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-center">
            <h3 className="font-semibold text-lg mb-2">{it.name}</h3>
            <p className="text-2xl font-bold text-emerald-500 mb-3">{it.price}</p>
            <p className="text-sm text-slate-600 dark:text-slate-300">{it.description}</p>
          </div>
        ))}
      </div>
    );
  }
  if (field.style === "qa") {
    return (
      <div className="space-y-4">
        {arr.map((it, i) => (
          <div key={i} className="p-4 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
            <p className="font-semibold mb-1">
              <span className="text-emerald-500 mr-2">Q.</span>
              {it.question}
            </p>
            <p className="text-slate-600 dark:text-slate-300">
              <span className="text-slate-400 mr-2">A.</span>
              {it.answer}
            </p>
          </div>
        ))}
      </div>
    );
  }
  // sections (privacy / terms)
  return (
    <div className="space-y-5">
      {arr.map((it, i) => (
        <div key={i}>
          <h3 className="text-lg font-semibold mt-4 mb-1">{it.heading}</h3>
          <Paragraphs text={it.body} className="text-slate-600 dark:text-slate-300 leading-relaxed" />
        </div>
      ))}
    </div>
  );
};

// スキーマに基づく構造化コンテンツのレンダラー
const renderContent = (slug, content) => {
  const schema = SITE_SCHEMAS[slug];
  const c = content || {};
  if (!schema) return null;
  return schema.map((field) => {
    const val = c[field.key];
    if (field.type === "text" || field.type === "textarea") {
      if (!val) return null;
      return (
        <section key={field.key} className="mb-6">
          {field.showLabel && <h2 className="text-2xl font-bold mt-8 mb-3">{field.label}</h2>}
          <Paragraphs text={val} className="text-slate-600 dark:text-slate-300 leading-relaxed my-2" />
        </section>
      );
    }
    if (field.type === "list") {
      const arr = Array.isArray(val) ? val : [];
      if (!arr.length) return null;
      return (
        <section key={field.key} className="mb-6">
          {field.showLabel && <h2 className="text-2xl font-bold mt-8 mb-3">{field.label}</h2>}
          <ul className="list-disc pl-6 space-y-1 text-slate-600 dark:text-slate-300">
            {arr.map((it, i) => (
              <li key={i}>{it}</li>
            ))}
          </ul>
        </section>
      );
    }
    if (field.type === "kvlist") {
      const arr = Array.isArray(val) ? val : [];
      if (!arr.length) return null;
      return (
        <section key={field.key} className="mb-6">
          {field.showLabel && <h2 className="text-2xl font-bold mt-8 mb-3">{field.label}</h2>}
          <div className="rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
            {arr.map((row, i) => (
              <div key={i} className="flex border-b border-slate-200 dark:border-slate-800 last:border-0">
                <div className="w-1/3 bg-slate-100 dark:bg-slate-900 px-4 py-3 text-sm font-medium">{row.label}</div>
                <div className="flex-1 px-4 py-3 text-sm text-slate-600 dark:text-slate-300">{row.value}</div>
              </div>
            ))}
          </div>
        </section>
      );
    }
    if (field.type === "cards") {
      const arr = Array.isArray(val) ? val : [];
      if (!arr.length) return null;
      return (
        <section key={field.key} className="mb-6">
          {field.showLabel && <h2 className="text-2xl font-bold mt-8 mb-3">{field.label}</h2>}
          {renderCards(field, arr)}
        </section>
      );
    }
    return null;
  });
};

// 運営会社情報（operating_company）から会社情報テーブルを生成
const kvTable = (rows) => (
  <div className="rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
    {rows.map((row, i) => (
      <div key={i} className="flex border-b border-slate-200 dark:border-slate-800 last:border-0">
        <div className="w-1/3 bg-slate-100 dark:bg-slate-900 px-4 py-3 text-sm font-medium">{row.label}</div>
        <div className="flex-1 px-4 py-3 text-sm text-slate-600 dark:text-slate-300 whitespace-pre-wrap">{row.value}</div>
      </div>
    ))}
  </div>
);

const buildAddress = (c) => `${c.postal_code ? "〒" + c.postal_code + " " : ""}${c.address || ""}`.trim();

const toRows = (pairs) =>
  pairs.filter(([, v]) => v && String(v).trim()).map(([label, value]) => ({ label, value }));

const companyAboutRows = (c) =>
  toRows([
    ["会社名", c.company_name],
    ["代表者", c.representative_name],
    ["所在地", buildAddress(c)],
    ["電話番号", c.phone],
    ["メールアドレス", c.email],
    ["Webサイト", c.website],
    ["インボイス登録番号", c.invoice_number],
  ]);

const companyTokushohoRows = (c) =>
  toRows([
    ["販売事業者名", c.company_name],
    ["運営統括責任者", c.representative_name],
    ["所在地", buildAddress(c)],
    ["電話番号", c.phone],
    ["FAX番号", c.fax],
    ["メールアドレス", c.email],
    ["URL", c.website],
    ["インボイス登録番号", c.invoice_number],
  ]);

export default function SitePage() {
  const { slug } = useParams();
  const { logoUrl } = useLogo();
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [navPages, setNavPages] = useState([]);
  const [company, setCompany] = useState({});
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    setLoading(true);
    setSubmitted(false);
    window.scrollTo(0, 0);
    Promise.all([
      axios.get(`${API}/site/pages/${slug}`),
      axios.get(`${API}/site/pages`),
      axios.get(`${API}/site/company`).catch(() => ({ data: {} })),
    ])
      .then(([pRes, lRes, cRes]) => {
        setPage(pRes.data);
        setNavPages((lRes.data || []).filter((p) => p.show_in_nav));
        setCompany(cRes.data || {});
      })
      .catch(() => setPage(null))
      .finally(() => setLoading(false));
  }, [slug]);

  const submitContact = async () => {
    if (!form.name || !form.email || !form.message) {
      toast.error("お名前・メールアドレス・お問い合わせ内容は必須です");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/site/contact`, form);
      toast.success(res.data.message || "送信しました");
      setSubmitted(true);
      setForm({ name: "", email: "", company: "", phone: "", message: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "送信に失敗しました");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex flex-col">
      <PublicHeader />

      <main className="flex-1">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
          </div>
        ) : !page ? (
          <div className="max-w-3xl mx-auto px-4 py-24 text-center">
            <h1 className="text-2xl font-bold mb-4">ページが見つかりません</h1>
            <Button onClick={() => navigate("/")}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              ホームへ戻る
            </Button>
          </div>
        ) : (
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-8" data-testid="site-page-title">
              {page.title}
            </h1>
            <div>{renderContent(slug, page.content)}</div>

            {slug === "about" && companyAboutRows(company).length > 0 && (
              <section className="mb-6" data-testid="about-company-info">
                <h2 className="text-2xl font-bold mt-8 mb-3">会社情報</h2>
                {kvTable(companyAboutRows(company))}
              </section>
            )}

            {slug === "tokushoho" && (
              <div data-testid="tokushoho-table">
                {companyTokushohoRows(company).length > 0 ? (
                  kvTable(companyTokushohoRows(company))
                ) : (
                  <p className="text-slate-500 dark:text-slate-400">運営会社情報が未設定です。SaaS管理コンソールの「運営会社」から入力してください。</p>
                )}
              </div>
            )}

            {page.is_contact && (
              <div className="mt-10 p-6 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800" data-testid="contact-form">
                {submitted ? (
                  <div className="text-center py-8">
                    <p className="text-lg font-semibold text-emerald-600">送信ありがとうございました。</p>
                    <p className="text-slate-500 mt-2">担当者より折り返しご連絡いたします。</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium">
                          お名前 <span className="text-red-500">*</span>
                        </label>
                        <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name" className="mt-1" />
                      </div>
                      <div>
                        <label className="text-sm font-medium">会社名</label>
                        <Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="contact-company" className="mt-1" />
                      </div>
                      <div>
                        <label className="text-sm font-medium">
                          メールアドレス <span className="text-red-500">*</span>
                        </label>
                        <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email" className="mt-1" />
                      </div>
                      <div>
                        <label className="text-sm font-medium">電話番号</label>
                        <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="contact-phone" className="mt-1" />
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium">
                        お問い合わせ内容 <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        value={form.message}
                        onChange={(e) => setForm({ ...form, message: e.target.value })}
                        rows={5}
                        data-testid="contact-message"
                        className="mt-1 w-full rounded-md border border-slate-300 dark:border-slate-700 bg-transparent px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                    <Button onClick={submitContact} disabled={submitting} className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700" data-testid="contact-submit">
                      {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                      送信する
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </main>

      <PublicFooter />
    </div>
  );
}
